import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { toast, Toaster } from 'sonner';
import { Plus, Minus, Save } from 'lucide-react';

export default function AdminInventory() {
    const [rows, setRows] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [edits, setEdits] = useState({});

    const reload = async () => {
        const [s, st, m] = await Promise.all([
            api.get('/stock/summary'),
            api.get('/stock'),
            api.get('/manufacturers'),
        ]);
        setSummary(s.data);
        setRows(st.data);
        setManufacturers(m.data);
    };

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    const productGroups = useMemo(() => {
        const groups = {};
        rows.forEach((r) => {
            if (!groups[r.product_slug]) groups[r.product_slug] = { name: r.product_name, items: [] };
            groups[r.product_slug].items.push(r);
        });
        return groups;
    }, [rows]);

    const visible = useMemo(() => {
        let out = rows;
        if (filter === 'low') out = out.filter((r) => r.on_hand > 0 && r.on_hand <= r.low_threshold);
        else if (filter === 'out') out = out.filter((r) => r.on_hand <= 0);
        else if (filter === 'in') out = out.filter((r) => r.on_hand > r.low_threshold);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            out = out.filter((r) =>
                r.sku.toLowerCase().includes(q) ||
                r.product_name.toLowerCase().includes(q) ||
                r.variant_label.toLowerCase().includes(q));
        }
        return out;
    }, [rows, filter, search]);

    const adjust = async (row, delta) => {
        try {
            const r = await api.post(`/stock/${row.id}/adjust`, { delta, reason: 'Manual adjustment' });
            setRows((prev) => prev.map((p) => (p.id === row.id ? r.data : p)));
            toast.success(`${row.sku} ${delta > 0 ? '+' : ''}${delta}`);
            const s = await api.get('/stock/summary');
            setSummary(s.data);
        } catch {
            toast.error('Adjust failed');
        }
    };

    const saveRow = async (row) => {
        const draft = edits[row.id];
        if (!draft) return;
        try {
            const body = {};
            if (draft.on_hand !== undefined) body.on_hand = parseInt(draft.on_hand || '0', 10);
            if (draft.low_threshold !== undefined) body.low_threshold = parseInt(draft.low_threshold || '0', 10);
            if (draft.manufacturer_id !== undefined) body.manufacturer_id = draft.manufacturer_id;
            const r = await api.patch(`/stock/${row.id}`, body);
            setRows((prev) => prev.map((p) => (p.id === row.id ? r.data : p)));
            setEdits((e) => { const c = { ...e }; delete c[row.id]; return c; });
            toast.success(`Saved ${row.sku}`);
            const s = await api.get('/stock/summary');
            setSummary(s.data);
        } catch {
            toast.error('Save failed');
        }
    };

    const setDraft = (id, key, val) => {
        setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: val } }));
    };

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;

    const statusOf = (r) => {
        if (r.on_hand <= 0) return { label: 'OUT', cls: 'text-danger border-danger/40' };
        if (r.on_hand <= r.low_threshold) return { label: 'LOW', cls: 'text-amber-400 border-amber-400/40' };
        return { label: 'OK', cls: 'text-brand border-brand/40' };
    };

    return (
        <div data-testid="admin-inventory">
            <Toaster theme="dark" />
            {/* Summary */}
            <section className="grid grid-cols-2 md:grid-cols-4 border-hairline mb-8">
                {[
                    ['SKUs tracked', summary?.total_skus ?? 0],
                    ['Total units', summary?.total_units ?? 0],
                    ['Low stock', summary?.low_stock ?? 0, 'text-amber-400'],
                    ['Out of stock', summary?.out_of_stock ?? 0, 'text-danger'],
                ].map(([l, v, color]) => (
                    <div key={l} className="p-5 border-r border-zinc-800 last:border-r-0">
                        <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">{l}</div>
                        <div className={`font-display text-2xl ${color || ''}`}>{v}</div>
                    </div>
                ))}
            </section>

            {/* Filters */}
            <section className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex gap-1">
                    {[['all', 'All'], ['in', 'In stock'], ['low', 'Low'], ['out', 'Out']].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)}
                                data-testid={`stock-filter-${k}`}
                                className={`font-mono-data text-[10px] uppercase px-3 py-1.5 border ${
                                    filter === k ? 'border-brand text-brand' : 'border-zinc-800 text-zinc-500 hover:text-white'
                                }`}>
                            {l}
                        </button>
                    ))}
                </div>
                <input
                    data-testid="stock-search"
                    placeholder="Search SKU, product, variant…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-surface border border-zinc-800 px-3 py-2 font-mono-data text-sm text-white w-full md:w-80" />
            </section>

            {/* Stock table */}
            <section className="border-hairline">
                <div className="grid grid-cols-12 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="col-span-3">Product / SKU</div>
                    <div className="col-span-3">Variant</div>
                    <div className="col-span-2 text-center">On hand</div>
                    <div className="col-span-1 text-center">Min</div>
                    <div className="col-span-2">Manufacturer</div>
                    <div className="col-span-1 text-right">Status</div>
                </div>
                <div className="max-h-[68vh] overflow-y-auto">
                    {visible.length === 0 && (
                        <div className="p-8 text-center text-zinc-500 font-mono-data text-sm">No SKUs match.</div>
                    )}
                    {visible.map((r) => {
                        const st = statusOf(r);
                        const draft = edits[r.id] || {};
                        const dirty = Object.keys(draft).length > 0;
                        return (
                            <div key={r.id} data-testid={`stock-row-${r.sku}`}
                                 className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60 hover:bg-surface/40">
                                <div className="col-span-3">
                                    <div className="font-display uppercase text-xs tracking-tight">{r.product_name}</div>
                                    <div className="font-mono-data text-[10px] text-zinc-500">{r.sku}</div>
                                </div>
                                <div className="col-span-3 font-body text-xs text-zinc-300">{r.variant_label}</div>
                                <div className="col-span-2 flex items-center justify-center gap-1">
                                    <button onClick={() => adjust(r, -1)}
                                            data-testid={`stock-minus-${r.sku}`}
                                            className="p-1 border border-zinc-800 hover:border-danger hover:text-danger">
                                        <Minus size={12} />
                                    </button>
                                    <input
                                        type="number" min="0"
                                        data-testid={`stock-input-${r.sku}`}
                                        value={draft.on_hand !== undefined ? draft.on_hand : r.on_hand}
                                        onChange={(e) => setDraft(r.id, 'on_hand', e.target.value)}
                                        className="w-16 bg-surface border border-zinc-800 px-2 py-1 text-center font-mono-data text-sm" />
                                    <button onClick={() => adjust(r, 1)}
                                            data-testid={`stock-plus-${r.sku}`}
                                            className="p-1 border border-zinc-800 hover:border-brand hover:text-brand">
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <div className="col-span-1 text-center">
                                    <input
                                        type="number" min="0"
                                        value={draft.low_threshold !== undefined ? draft.low_threshold : r.low_threshold}
                                        onChange={(e) => setDraft(r.id, 'low_threshold', e.target.value)}
                                        className="w-12 bg-surface border border-zinc-800 px-2 py-1 text-center font-mono-data text-xs" />
                                </div>
                                <div className="col-span-2">
                                    <select
                                        value={draft.manufacturer_id !== undefined ? draft.manufacturer_id : (r.manufacturer_id || '')}
                                        onChange={(e) => setDraft(r.id, 'manufacturer_id', e.target.value)}
                                        className="w-full bg-surface border border-zinc-800 px-2 py-1 font-mono-data text-xs">
                                        <option value="">—</option>
                                        {manufacturers.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-span-1 flex items-center justify-end gap-2">
                                    <span className={`inline-block px-2 py-1 border font-mono-data text-[10px] uppercase ${st.cls}`}>
                                        {st.label}
                                    </span>
                                    {dirty && (
                                        <button onClick={() => saveRow(r)}
                                                data-testid={`stock-save-${r.sku}`}
                                                className="p-1.5 bg-brand hover:bg-brand-hover text-black">
                                            <Save size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="mt-8 font-mono-data text-[10px] uppercase text-zinc-500">
                Tip: use +/− for single-unit adjustments (logged automatically). Edit the number then click the save
                icon to set an exact count (used for stock takes). Min = low-stock threshold.
            </section>

            <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(productGroups).map(([slug, g]) => {
                    const totalOH = g.items.reduce((s, i) => s + i.on_hand, 0);
                    const totalSKUs = g.items.length;
                    const lastCounted = g.items
                        .map((i) => i.last_counted)
                        .filter(Boolean)
                        .sort()
                        .pop();
                    return (
                        <div key={slug} className="border-hairline p-4">
                            <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">{slug}</div>
                            <div className="font-display uppercase text-sm mb-3">{g.name}</div>
                            <div className="flex justify-between font-mono-data text-xs">
                                <span className="text-zinc-400">{totalSKUs} SKUs</span>
                                <span className="text-brand">{totalOH} units</span>
                            </div>
                            <div className="font-mono-data text-[10px] text-zinc-500 mt-2">
                                Last count: {lastCounted ? fmtDate(lastCounted) : 'never'}
                            </div>
                        </div>
                    );
                })}
            </section>
        </div>
    );
}
